<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @before-hide="unbindIntegrationReferences"
  >
    <q-card
      class="integration-editor"
    >
      <q-form
        ref="form"
        @submit="onSubmitForm"
      >
        <q-card-section class="row q-col-gutter-md">
          <div
            class="col-12"
          >
            <q-table
              v-model:pagination="pagination"
              flat
              :rows="isRefreshing ? [] : referenceRows"
              :columns="columns"
              row-key="refId"
              selection="single"
              :filter="searchFilter"
              :loading="isRefreshing || isLoadingCache"
              @row-click="onSelectRow"
            >
              <template #top>
                <div class="row full-width items-center q-col-gutter-md">
                  <div class="col text-h6 ellipsis">
                    {{ $t('settings.integrations.clients.textTitle', {
                      integration: getIntegrationName(connectedClinic.integration)
                    }) }}
                  </div>
                  <div v-if="isPollingDrivenIntegration">
                    <q-btn
                      :label="$t('settings.integrations.clients.buttonRefresh')"
                      flat
                      color="primary"
                      :loading="isRefreshing || isLoadingCache"
                      icon="refresh"
                      @click="onRefresh"
                    />
                  </div>
                  <q-input
                    v-model="searchFilter"
                    borderless
                    dense
                    clearable
                    debounce="300"
                    :placeholder="$t('common.buttonSearch')"
                  >
                    <template #append>
                      <q-icon
                        name="search"
                        color="primary"
                      />
                    </template>
                  </q-input>
                </div>
                <div class="col-12 q-mt-md">
                  <banner
                    v-if="isIntegrationRefDeleted"
                    type="warning"
                    :message="$t('settings.integrations.textIntegrationRefDeleted', {
                      integration: getIntegrationName(connectedClinic.integration)
                    })"
                  />
                </div>
              </template>
              <template #body-selection="props">
                <q-radio
                  :model-value="selectedIntegrationReference"
                  :val="props.row.refId"
                  :disable="!isRefAvailable(props.row)"
                  @click="onSelectRow(null, props.row)"
                >
                  <q-tooltip
                    v-if="!isRefAvailable(props.row)"
                  >
                    {{ cantBindTooltipText }}
                  </q-tooltip>
                </q-radio>
              </template>
            </q-table>
          </div>
        </q-card-section>
        <q-card-actions
          v-if="!isSupervisor"
        >
          <q-btn
            v-if="!showAllReferences"
            flat
            :label="$t('settings.integrations.buttonShowAllReferences')"
            color="primary"
            @click="showAllReferences = !showAllReferences"
          />
        </q-card-actions>
        <q-card-actions>
          <q-btn
            v-close-popup
            flat
            :label="$t(
              'settings.integrations.buttonUnlinkProfile',
              { integration: getIntegrationName(connectedClinic.integration) })
            "
            :disable="!selectedIntegrationReference"
            @click="onUnlinkProfile"
          />
          <q-space />
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="secondary"
            @click="hide"
          />
          <q-btn
            flat
            :label="$t('common.buttonSave')"
            type="submit"
            color="primary"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import Banner from 'components/UI/Banner';
import { chain, find, get, keys } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { BASE_INTEGRATIONS, POLLING_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import ClinicClientReferencesDb from 'src/fb/clinicClientReferences-db';
import ClinicUserReferencesDb from 'src/fb/clinicUserReferences-db';
import IntegrationCacheDB from 'src/fb/integrationCache-db';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import { notifyErrorWithDetails } from 'src/utils/AppUtils';
import { auth } from 'src/store/auth';
import { integrationReferences as useIntegrationReferencesStore } from 'src/store/integrationReferences';

export default {
  name: 'EditIntegration',

  components: { Banner },

  mixins: [Integrations, Interactions],

  props: {
    objectType: {
      type: String,
      required: true,
    },

    integrationReference: {
      type: [String, Number],
      default: null,
    },

    savedIntegrationReference: {
      type: [String, Number],
      default: null,
    },

    clientId: {
      type: String,
      default: null,
    },

    userId: {
      type: String,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      isRefreshing: false,
      isLoadingCache: false,
      hasLoadError: false,

      showAllReferences: false,

      selectedIntegrationReference: null,
      references: [],
      groupedRefNames: [],

      searchFilter: null,

      pagination: {
        rowsPerPage: 25,
        sortBy: 'fullName',
        page: 1,
      },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'isSupervisor']),
    ...mapState(useIntegrationReferencesStore, ['getReferences', 'getLinkedNames']),

    /**
     * These integrations own their references through a cron and the Refresh button, so the list
     * can be read once and cached. The event-driven ones stay on a live binding.
     */
    isPollingDrivenIntegration() {
      return POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration);
    },

    columns() {
      const fullNameColumn = {
        name: 'fullName',
        field: 'fullName',
        required: true,
        label: this.$t('settings.integrations.clients.columnClientName'),
        align: 'left',
        sortable: true,
      };
      const emailColumn = {
        name: 'email',
        field: 'email',
        required: true,
        label: this.$t('settings.integrations.clients.columnEmail'),
        align: 'left',
        sortable: true,
      };
      const refIdColumn = {
        name: 'refId',
        field: 'refId',
        required: false,
        label: this.$t('settings.integrations.clients.columnReference', {
          integration: this.getIntegrationName(get(this.connectedClinic, 'integration')),
        }),
        align: 'left',
        sortable: true,
      };

      const connectedInstanceColumns = this.showAllReferences ? [{
        name: 'connectedInstance',
        field: (row) => this.groupedRefNames[row.refId],
        required: false,
        label: this.$t(`settings.integrations.${this.userId ? 'users' : 'clients'}.columnConnectedTo`),
        align: 'left',
        sortable: true,
      }] : [];

      if (this.objectType === 'staff') {
        return [
          fullNameColumn,
          emailColumn,
          refIdColumn,
          ...connectedInstanceColumns,
        ];
      }
      return [
        fullNameColumn,
        refIdColumn,
        ...connectedInstanceColumns,
      ];
    },

    availableReferences() {
      const linkedReferences = keys(this.groupedRefNames);

      return this.references.filter(({ refId, active }) => {
        if (active === false && !this.isCurrentOrSavedReference(refId)) {
          // exclude clients inactive on the integration side, unless it's the client's current
          // reference — otherwise the row silently disappears instead of showing as unlinked (HR-4833)
          return false;
        }
        return this.isReferenceAvailableOrUnlinked(linkedReferences, refId);
      });
    },

    referenceRows() {
      return (this.showAllReferences)
        ? this.references
        : this.availableReferences;
    },

    cantBindTooltipText() {
      return this.userId
        ? this.$t('users.modals.editUser.userAlreadyHasBoundReference')
        : this.$t('clients.modals.editClient.clientAlreadyHasBoundReference');
    },

    isIntegrationRefDeleted() {
      return !(this.isLoadingCache || this.isRefreshing || this.hasLoadError)
        && !!this.integrationReference
        && !find(this.references, { refId: this.integrationReference });
    },
  },

  async created() {
    this.selectedIntegrationReference = this.integrationReference;
    await this.loadIntegrationReferences();
  },

  methods: {
    ...mapActions(useIntegrationReferencesStore, ['loadReferences']),

    /**
     * Both halves of the list are useless on their own, so a failure in either has to surface.
     * Without this the table just renders empty, which reads as "this integration has no profiles".
     */
    async loadIntegrationReferences({ force = false } = {}) {
      this.hasLoadError = false;

      try {
        if (this.isPollingDrivenIntegration) {
          await this.loadCachedReferences(force);
          return;
        }

        await this.getReferencesLinks();
        await this.bindIntegrationReferences();
      } catch (err) {
        this.hasLoadError = true;
        this.notifyNegative(
          this.$t('common.errors.databaseReadError'),
          {},
          { copyActionValue: err.details?.fullMessage || err.message },
        );
      }
    }, // loadIntegrationReferences()

    async loadCachedReferences(force = false) {
      this.isLoadingCache = true;

      try {
        await this.loadReferences(this.objectType, { force });

        this.references = this.getReferences(this.objectType);
        this.groupedRefNames = this.getLinkedNames(this.objectType);
        this.goToSelected();
      } finally {
        this.isLoadingCache = false;
      }
    }, // loadCachedReferences()

    show() {
      this.$refs.dialog.show();
    }, // show()

    async hide() {
      await this.unbindIntegrationReferences();
      this.$emit('hide');

      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    isRefAvailable(reference) {
      return (this.integrationReference === reference.refId)
        || (this.savedIntegrationReference === reference.refId)
        || (!!find(this.availableReferences, (availableRef) => (reference.refId === availableRef.refId)));
    },

    async getReferencesLinks() {
      this.isLoadingCache = true;

      try {
        const { data } = await callFirebaseFunction('getReferencesLinks', { referenceType: this.objectType });
        this.groupedRefNames = data;
      } finally {
        this.isLoadingCache = false;
      }
    },

    onSubmitForm() {
      const selectedItem = find(this.references, { refId: this.selectedIntegrationReference }) || null;
      this.$emit('ok', selectedItem);
      this.hide();
    }, // onSubmitForm()

    async onRefresh() {
      this.isRefreshing = true;
      try {
        await callFirebaseFunction('refreshIntegrationRefs', {
          clinicId: this.userDetails.clinic,
          objectType: this.objectType,
        }, { timeout: 8 * 60 * 1000 });

        if (this.isPollingDrivenIntegration) {
          await this.loadCachedReferences(true);
          this.hasLoadError = false;
          return;
        }

        await this.unbindIntegrationReferences();
        await this.getReferencesLinks();
        await this.bindIntegrationReferences();
      } catch (err) {
        // Refresh bypasses loadIntegrationReferences(), the only other place this flag changes —
        // without this, a failed initial load leaves it true forever, permanently hiding the
        // deleted-reference banner even after a later refresh brings back valid data.
        this.hasLoadError = true;
        notifyErrorWithDetails(err.message, err.details?.fullMessage);
      } finally {
        this.isRefreshing = false;
      }
    }, // onRefresh()

    onSelectRow(evt, row) {
      if (!this.isRefAvailable(row)) {
        return;
      }

      if (this.selectedIntegrationReference === get(row, 'refId')) {
        this.selectedIntegrationReference = null;
      } else {
        this.selectedIntegrationReference = get(row, 'refId');
      }
    },

    async bindIntegrationReferences() {
      this.isLoadingCache = true;

      if (!BASE_INTEGRATIONS.includes(this.connectedClinic.integration)) {
        const clinicIntegrationReferencesDb = this.objectType === 'staff'
          ? new ClinicUserReferencesDb(this.userDetails.clinic, this.connectedClinic.integration)
          : new ClinicClientReferencesDb(this.userDetails.clinic, this.connectedClinic.integration);

        try {
          await clinicIntegrationReferencesDb.bindAll(
            this,
            'references',
          );
          this.goToSelected();
        } finally {
          this.isLoadingCache = false;
        }

        return;
      }

      const integrationCacheDb = new IntegrationCacheDB();
      try {
        await integrationCacheDb.bindAll(
          this,
          'references',
          [
            ['clinic', '==', this.userDetails.clinic],
            ['type', '==', this.objectType],
          ],
        );
        this.goToSelected();
      } finally {
        this.isLoadingCache = false;
      }
    }, // bindIntegrationReferences()

    unbindIntegrationReferences() {
      if (this.isPollingDrivenIntegration) {
        return null;
      }

      const integrationCacheDb = new IntegrationCacheDB();
      return integrationCacheDb.unbind(this, 'references');
    },

    goToSelected() {
      if (this.searchFilter || !this.selectedIntegrationReference || !this.pagination.rowsPerPage) {
        return;
      }
      const selectedIndex = chain(this.availableReferences)
        .orderBy(this.pagination.sortBy, this.pagination.descending ? 'desc' : 'asc')
        .findIndex({ refId: this.selectedIntegrationReference })
        .value();

      this.pagination.page = Math.floor(selectedIndex / this.pagination.rowsPerPage) + 1;
    },

    onUnlinkProfile() {
      this.selectedIntegrationReference = null;
      this.$emit('ok', null);
    },

    isReferenceAvailableOrUnlinked(linkedReferences, refId) {
      if (refId === null || refId === undefined) {
        return false;
      }

      const refIdString = refId.toString();
      return this.isCurrentOrSavedReference(refId) || !linkedReferences.includes(refIdString);
    }, // isReferenceAvailable()

    isCurrentOrSavedReference(refId) {
      if (refId === null || refId === undefined) {
        return false;
      }

      const refIdString = refId.toString();
      return this.integrationReference?.toString() === refIdString
        || this.savedIntegrationReference?.toString() === refIdString;
    },

  },
};
</script>
<style lang="scss" scoped>
.integration-editor {
  min-width:70vw;
}
</style>
